/**
 * Multi-tab liveness detection for the "logout on close" behaviour.
 *
 * Instead of trying to detect the *last* tab closing (which can only run during teardown, where
 * async work is unreliable), we detect at tab *startup* whether any OTHER tab is currently alive.
 * If a peer is alive the current tab adopts the existing session; if none is alive the tab is
 * treated as the first of a fresh browser session.
 *
 * Primary signal: a per-tab Web Lock held for the tab's entire lifetime. The browser releases the
 * lock automatically when the tab is closed or crashes, so `navigator.locks.query()` reflects
 * exactly the set of live tabs. A BroadcastChannel ping/pong provides a fallback for browsers
 * without the Web Locks API.
 */

const LOCK_PREFIX = 'vivli_tab_';
const CHANNEL_NAME = 'vivli_auth_sync';
const PING_ALIVE = 'PING_ALIVE';
const PONG_ALIVE = 'PONG_ALIVE';
const PEER_WAIT_MS = 200;

const hasCrypto = typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function';
const tabId = hasCrypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
const lockName = `${LOCK_PREFIX}${tabId}`;

const locksSupported = typeof navigator !== 'undefined' && !!navigator.locks;
const channelSupported = typeof BroadcastChannel !== 'undefined';

let channel: BroadcastChannel | null = null;
let acquired = false;
let releaseLock: (() => void) | null = null;

const getChannel = (): BroadcastChannel | null => {
    if (!channelSupported) return null;
    if (!channel) {
        channel = new BroadcastChannel(CHANNEL_NAME);
    }
    return channel;
};

// Persistent responder so this (alive) tab answers other tabs' startup pings.
const pingResponder = (event: MessageEvent) => {
    if (event.data?.type === PING_ALIVE && event.data?.sender !== tabId) {
        getChannel()?.postMessage({ type: PONG_ALIVE, sender: tabId });
    }
};

/**
 * Acquire this tab's lifetime lock and start answering peer pings. Idempotent.
 */
export const acquireTabLock = (): void => {
    if (acquired) return;
    acquired = true;

    getChannel()?.addEventListener('message', pingResponder);

    if (locksSupported) {
        // Held until releaseLock() is called (explicit logout) or the browser reclaims it on close.
        navigator.locks.request(
            lockName,
            () =>
                new Promise<void>((resolve) => {
                    releaseLock = resolve;
                })
        );
    }
};

/**
 * True if another tab is currently alive.
 */
export const hasLivePeer = async (): Promise<boolean> => {
    if (locksSupported) {
        try {
            const { held, pending } = await navigator.locks.query();
            const all = [...(held ?? []), ...(pending ?? [])];
            return all.some((l) => l.name?.startsWith(LOCK_PREFIX) && l.name !== lockName);
        } catch {
            // fall through to the channel-based check
        }
    }

    const ch = getChannel();
    if (!ch) return false;

    return new Promise<boolean>((resolve) => {
        let peerFound = false;
        const onPong = (event: MessageEvent) => {
            if (event.data?.type === PONG_ALIVE && event.data?.sender !== tabId) {
                peerFound = true;
            }
        };
        ch.addEventListener('message', onPong);
        ch.postMessage({ type: PING_ALIVE, sender: tabId });

        setTimeout(() => {
            ch.removeEventListener('message', onPong);
            resolve(peerFound);
        }, PEER_WAIT_MS);
    });
};

/**
 * Release this tab's lock and stop responding to pings (called on explicit logout).
 */
export const releaseTab = (): void => {
    releaseLock?.();
    releaseLock = null;
    getChannel()?.removeEventListener('message', pingResponder);
    acquired = false;
};
